import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  ClinicAgentSocket,
  ClinicMessagePayload,
  ClinicSocketState,
} from '../services/clinicAgentSocket';
import { clinicChatHistoryService } from '../services/clinicChatHistoryService';
import { resolveProposals } from '../services/clinicAgentProposals';
import { patientsService } from '../services/patientsService';
import {
  ChatMessage,
  Proposal,
  ProposalResolveResult,
  ProposalState,
  ProposalStatus,
} from '../models/clinicAgent';

// The patient the chat is currently targeting. Name is cosmetic (for the pill);
// only `id` is sent to the backend as patient_id.
export type FocusedPatient = { id: number; name: string };

type ClinicAgentContextValue = {
  messages: ChatMessage[];
  connectionState: ClinicSocketState;
  typing: boolean;
  statusLabel: string | null;
  isBusy: boolean;
  authError: boolean;
  sendMessage: (
    text: string,
    file?: { dataUrl: string; type: string; name: string },
  ) => void;
  // Resend a user message whose send previously failed (see ChatMessage.sendState).
  retrySend: (messageId: string) => void;
  // Patient focus: which patient messages target. Unlocked focus mirrors the
  // open patient page and auto-switches as the clinician navigates; locking
  // pins the current patient so it survives navigation until cleared.
  focusedPatient: FocusedPatient | null;
  focusLocked: boolean;
  // Pin the current target so it survives navigation.
  lockFocus: () => void;
  // Release the pin and re-sync to the patient page currently in view.
  unlockFocus: () => void;
  // Focus an explicitly chosen patient (from the picker). Does NOT lock — the
  // lock is only ever enabled by the clinician via lockFocus.
  focusPatient: (patient: FocusedPatient) => void;
  // Drop the current target; stays empty until the next patient page is opened.
  clearFocus: () => void;
  // Called by the panel with the patient id from the current route.
  syncRoutePatient: (patientId?: number) => void;
  resolveProposal: (proposalId: string, decision: 'accept' | 'reject') => Promise<void>;
  resolveManyProposals: (proposalIds: string[], decision: 'accept' | 'reject') => Promise<void>;
  hasMoreHistory: boolean;
  loadingMoreHistory: boolean;
  loadMoreHistory: () => Promise<void>;
};

const ClinicAgentContext = createContext<ClinicAgentContextValue | undefined>(undefined);

const CLINICIAN_ROLES = ['doctor', 'nutritionist', 'admin'];

// Pages to pull on first hydrate (~20 rows each → ~100 messages), enough to make
// the transcript scrollable so the scroll-up "load older" trigger can engage.
const INITIAL_HISTORY_PAGES = 5;

// Safety net for a dead connection or server: a streamed reply with no delta,
// status or final message for this long is treated as lost and the turn ended.
// A crashed run normally ends with an `error` frame instead. Heavy turns can go
// well over a minute without any event (reasoning, or writing the arguments of
// many proposals), so this stays above the backend's 300 s OpenAI read timeout.
const STREAM_STALL_TIMEOUT_MS = 330_000;

const RUN_FAILED_NOTICE = 'La respuesta no se completó. Reenvía tu mensaje.';

// The in-progress reply of one run: the bubble it renders into and the model
// call whose text it currently holds (a new call restarts the text).
type ProvisionalReply = { messageId: string; call: number };

function makeId(): string {
  return `msg-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

// Map a server status onto a terminal lifecycle state, or null when the
// proposal isn't actually resolved (e.g. a failed apply that stays "pending").
function terminalStatus(status: string): 'applied' | 'discarded' | null {
  const s = (status || '').toLowerCase();
  if (['discarded', 'rejected', 'declined', 'cancelled', 'canceled'].includes(s)) {
    return 'discarded';
  }
  if (['applied', 'accepted', 'done', 'success', 'ok'].includes(s)) {
    return 'applied';
  }
  return null;
}

// Fold server-authored resolutions (proposal_resolved, broadcast to every tab)
// into the transcript. Proposals live on their own row now, so we match purely
// on proposal_id and flip the card's badge in place wherever it sits (live turn
// or persisted history row). This is idempotent — it also reconciles the acting
// tab's optimistic update. A resolution for a proposal outside the loaded window
// is a no-op; it reconciles from the proposal's own persisted status on reload.
function applyResolutionsToMessages(
  messages: ChatMessage[],
  results: ProposalResolveResult[],
): ChatMessage[] {
  let next = messages;
  for (const res of results) {
    if (!res.proposal_id) continue;
    const status = terminalStatus(res.status);
    if (!status) continue; // not resolved (failed/pending) — leave the card open
    next = next.map((m) => {
      if (!m.proposals) return m;
      let changed = false;
      const proposals = m.proposals.map((p) => {
        if (p.id !== res.proposal_id) return p;
        changed = true;
        return { ...p, status: status as ProposalStatus, error: undefined, detail: res.detail ?? p.detail };
      });
      return changed ? { ...m, proposals } : m;
    });
  }
  return next;
}

export const ClinicAgentProvider = ({ children }: { children: ReactNode }) => {
  const { user, access, refreshTokens } = useAuth();
  const isClinician = !!user && CLINICIAN_ROLES.includes(user.role);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connectionState, setConnectionState] = useState<ClinicSocketState>('closed');
  const [typing, setTyping] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [authError, setAuthError] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);

  // Patient focus (see ClinicAgentContextValue for semantics).
  const [focusedPatient, setFocusedPatient] = useState<FocusedPatient | null>(null);
  const [focusLocked, setFocusLocked] = useState(false);
  const focusedPatientRef = useRef<FocusedPatient | null>(null);
  focusedPatientRef.current = focusedPatient;
  const focusLockedRef = useRef(focusLocked);
  focusLockedRef.current = focusLocked;
  // Resolved patient names, so re-visiting a patient doesn't refetch the name.
  const patientNameCacheRef = useRef<Map<number, string>>(new Map());
  // Guards against a slow name fetch clobbering a newer focus target.
  const routeSyncSeqRef = useRef(0);
  // Patient id of the page currently in view (tracked even while pinned) so
  // unlocking can immediately resume mirroring it.
  const lastRoutePatientIdRef = useRef<number | undefined>(undefined);
  // Set when the clinician clears the target while a patient page is open: that
  // patient must not auto-reselect (you're still on their page, so no "entered a
  // patient" event will fire). Cleared once the route moves to a different one.
  const suppressedRoutePatientIdRef = useRef<number | undefined>(undefined);

  // Highest history page already merged into the transcript. Page 1 is the
  // newest slice (loaded on hydrate); each scroll-up fetches the next, older one.
  const historyPageRef = useRef(0);

  const activeRunIdRef = useRef<number | null>(null);
  const completedRunIdsRef = useRef<Set<number>>(new Set());
  // True between sending a message (or seeing typing/status) and the assistant
  // reply. A disconnect while awaiting means the turn is lost server-side, so we
  // clear the spinner and surface a notice instead of hanging forever.
  const awaitingResponseRef = useRef(false);
  const socketRef = useRef<ClinicAgentSocket | null>(null);
  const historyLoadedRef = useRef(false);
  // Payloads whose send failed, keyed by the optimistic message id, so a retry
  // resends the exact original frame (text + target + file) regardless of where
  // focus has moved since.
  const failedPayloadsRef = useRef<Map<string, ClinicMessagePayload>>(new Map());
  // Streamed replies still waiting for their run's final assistant_message.
  const provisionalRepliesRef = useRef<Map<number, ProvisionalReply>>(new Map());
  const streamStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshTokensRef = useRef(refreshTokens);
  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  const clearStreamStallTimer = useCallback(() => {
    if (streamStallTimerRef.current) {
      clearTimeout(streamStallTimerRef.current);
      streamStallTimerRef.current = null;
    }
  }, []);

  // Remove every provisional bubble whose run will never finish (disconnect,
  // failed run, or stalled run).
  const dropProvisionalReplies = useCallback(() => {
    clearStreamStallTimer();
    const ids = new Set(Array.from(provisionalRepliesRef.current.values(), (p) => p.messageId));
    provisionalRepliesRef.current.clear();
    if (ids.size > 0) setMessages((prev) => prev.filter((m) => !ids.has(m.id)));
  }, [clearStreamStallTimer]);

  // End the run in flight without a reply: drop its partial text, unlock the
  // input and leave a notice so the clinician can resend. Frames that still
  // trickle in for the failed run are ignored like those of a finished one.
  const endFailedTurn = useCallback(
    (notice: string) => {
      for (const runId of provisionalRepliesRef.current.keys()) completedRunIdsRef.current.add(runId);
      if (activeRunIdRef.current !== null) completedRunIdsRef.current.add(activeRunIdRef.current);
      dropProvisionalReplies();
      awaitingResponseRef.current = false;
      activeRunIdRef.current = null;
      setTyping(false);
      setStatusLabel(null);
      setMessages((prev) => [
        ...prev,
        { id: makeId(), role: 'assistant', text: notice, ts: Date.now(), messageType: 'system_notice' },
      ]);
    },
    [dropProvisionalReplies],
  );

  // (Re)start the stall countdown while a reply is streaming.
  const armStreamStallTimer = useCallback(() => {
    clearStreamStallTimer();
    streamStallTimerRef.current = setTimeout(() => {
      streamStallTimerRef.current = null;
      if (provisionalRepliesRef.current.size === 0) return;
      endFailedTurn(RUN_FAILED_NOTICE);
    }, STREAM_STALL_TIMEOUT_MS);
  }, [clearStreamStallTimer, endFailedTurn]);

  // Hydrate transcript from server history (GET /api/chat/) once per session.
  // The API serves ~20 rows/page, which often isn't tall enough to scroll — and
  // without a scrollbar the scroll-up "load older" trigger can never fire. So we
  // pull several pages up front (~100 messages) to guarantee a scrollable
  // transcript; further pages then load on demand as the clinician scrolls up.
  const loadHistory = useCallback(async () => {
    if (historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    try {
      let collected: ChatMessage[] = [];
      let page = 0;
      let more = true;
      while (page < INITIAL_HISTORY_PAGES && more) {
        page += 1;
        const { messages: pageMessages, hasMore } = await clinicChatHistoryService.getHistory(page);
        // Pages arrive newest-first; older pages prepend to stay chronological.
        collected = [...pageMessages, ...collected];
        more = hasMore;
      }
      historyPageRef.current = page;
      setHasMoreHistory(more);
      if (collected.length > 0) {
        setMessages((prev) => (prev.length === 0 ? collected : prev));
      }
    } catch (err) {
      console.warn('ClinicAgent: failed to load chat history', err);
    }
  }, []);

  // Fetch the next (older) page and prepend it. Older pages never overlap the
  // live socket messages, and rows carry stable `hist-<id>` ids, so a plain
  // prepend is safe — no dedupe needed.
  const loadMoreHistory = useCallback(async () => {
    if (loadingMoreHistory || !hasMoreHistory || !historyLoadedRef.current) return;
    setLoadingMoreHistory(true);
    const nextPage = historyPageRef.current + 1;
    try {
      const { messages: older, hasMore } = await clinicChatHistoryService.getHistory(nextPage);
      historyPageRef.current = nextPage;
      setHasMoreHistory(hasMore);
      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev]);
      }
    } catch (err) {
      console.warn('ClinicAgent: failed to load more chat history', err);
    } finally {
      setLoadingMoreHistory(false);
    }
  }, [loadingMoreHistory, hasMoreHistory]);

  // Socket lifecycle — only for authenticated clinicians.
  useEffect(() => {
    if (!access || !isClinician) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setConnectionState('closed');
      setTyping(false);
      setStatusLabel(null);
      activeRunIdRef.current = null;
      completedRunIdsRef.current.clear();
      provisionalRepliesRef.current.clear();
      clearStreamStallTimer();
      return;
    }

    if (socketRef.current) return;

    setAuthError(false);
    loadHistory();

    const socket = new ClinicAgentSocket(
      () => localStorage.getItem('access') || undefined,
      {
        onStateChange: (s) => {
          setConnectionState(s);
          if (s === 'closed' || s === 'reconnecting') {
            setStatusLabel(null);
            activeRunIdRef.current = null;
            // Partial replies of turns lost with the connection never complete.
            dropProvisionalReplies();
            // A dropped connection loses any in-flight turn server-side: the
            // reply will never arrive. Stop the spinner and tell the clinician
            // to resend, rather than leaving "Escribiendo…" up forever.
            if (awaitingResponseRef.current) {
              awaitingResponseRef.current = false;
              setTyping(false);
              setMessages((prev) => [
                ...prev,
                {
                  id: makeId(),
                  role: 'assistant',
                  text: 'Se perdió la conexión y la respuesta no se completó. Reenvía tu mensaje cuando se restablezca la conexión.',
                  ts: Date.now(),
                  messageType: 'system_notice',
                },
              ]);
            }
          }
        },
        onTyping: () => {
          awaitingResponseRef.current = true;
          setTyping(true);
        },
        onAgentStatus: ({ label, run_id }) => {
          // Drop late status frames for an already-completed run (no ghost chips).
          if (run_id !== null && completedRunIdsRef.current.has(run_id)) return;
          awaitingResponseRef.current = true;
          activeRunIdRef.current = run_id;
          setStatusLabel(label);
          // Still alive: a status mid-stream means the agent moved on to tools.
          if (run_id !== null && provisionalRepliesRef.current.has(run_id)) armStreamStallTimer();
        },
        onAssistantDelta: ({ run_id, call, text }) => {
          if (!Number.isFinite(run_id) || completedRunIdsRef.current.has(run_id)) return;
          awaitingResponseRef.current = true;
          activeRunIdRef.current = run_id;
          // Once text flows, the streamed reply replaces the status chip.
          setStatusLabel(null);
          armStreamStallTimer();
          const current = provisionalRepliesRef.current.get(run_id);
          if (!current) {
            const id = makeId();
            provisionalRepliesRef.current.set(run_id, { messageId: id, call });
            setMessages((prev) => [
              ...prev,
              { id, role: 'assistant', text, ts: Date.now(), runId: run_id, streaming: true },
            ]);
            return;
          }
          // A new call (a retry, or text written before tool calls) starts the
          // reply over; chunks never concatenate across calls.
          const restart = current.call !== call;
          current.call = call;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === current.messageId ? { ...m, text: restart ? text : m.text + text } : m,
            ),
          );
        },
        onAssistantMessage: ({ text, proposals, run_id }) => {
          if (Number.isFinite(run_id)) completedRunIdsRef.current.add(run_id);
          // The final message is authoritative over whatever was streamed.
          const provisional = provisionalRepliesRef.current.get(run_id);
          if (provisional) {
            provisionalRepliesRef.current.delete(run_id);
            if (provisionalRepliesRef.current.size === 0) clearStreamStallTimer();
          }
          awaitingResponseRef.current = false;
          setTyping(false);
          setStatusLabel(null);
          activeRunIdRef.current = null;
          const proposalStates: ProposalState[] = (proposals || []).map((p: Proposal) => ({
            ...p,
            run_id: p.run_id ?? (Number.isFinite(run_id) ? run_id : undefined),
            status: 'pending',
          }));
          // Render even when text is empty but proposals exist.
          if (text.trim() === '' && proposalStates.length === 0) {
            if (provisional) {
              setMessages((prev) => prev.filter((m) => m.id !== provisional.messageId));
            }
            return;
          }
          const reply: ChatMessage = {
            id: provisional?.messageId ?? makeId(),
            role: 'assistant',
            text,
            proposals: proposalStates.length > 0 ? proposalStates : undefined,
            ts: Date.now(),
            runId: Number.isFinite(run_id) ? run_id : undefined,
          };
          setMessages((prev) =>
            provisional && prev.some((m) => m.id === provisional.messageId)
              ? prev.map((m) => (m.id === provisional.messageId ? reply : m))
              : [...prev, reply],
          );
        },
        onRunError: ({ message }) => {
          // The backend's run crashed; no assistant_message will follow.
          endFailedTurn(message.trim() || RUN_FAILED_NOTICE);
        },
        onProactiveMessage: ({ text }) => {
          // Clinic agent shouldn't push, but render defensively if it ever does.
          awaitingResponseRef.current = false;
          setStatusLabel(null);
          activeRunIdRef.current = null;
          if (text.trim() === '') return;
          setMessages((prev) => [
            ...prev,
            { id: makeId(), role: 'assistant', text, ts: Date.now() },
          ]);
        },
        onProposalResolved: ({ results }) => {
          if (results.length === 0) return;
          setMessages((prev) => applyResolutionsToMessages(prev, results));
        },
        onProposalAck: ({ pending }) => {
          // Receipt confirmation for our proposal_result; nothing to render.
          console.debug('ClinicAgent: proposal_ack, pending =', pending);
        },
        onUnauthorized: () => {
          setAuthError(true);
        },
        refreshToken: async () => {
          try {
            const result = await refreshTokensRef.current();
            return !!result?.success;
          } catch (err) {
            console.warn('ClinicAgent: refreshTokens threw', err);
            return false;
          }
        },
      },
    );

    socketRef.current = socket;
    socket.connect();

    return () => {
      clearStreamStallTimer();
      socket.close();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [access, isClinician, loadHistory, armStreamStallTimer, clearStreamStallTimer, dropProvisionalReplies, endFailedTurn]);

  // Web lifecycle: browsers drop idle WS in background tabs and freeze timers,
  // so on tab-visible / back-online refresh the token then force a reconnect.
  useEffect(() => {
    if (!access || !isClinician) return;

    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (!socket) return;
      // The connection usually survives a backgrounded tab. Don't tear down a
      // healthy socket on every refocus — that would needlessly drop an
      // in-flight turn. Only refresh the token and reconnect when it actually
      // died while we were away.
      if (socket.isOpen) return;
      try {
        await refreshTokensRef.current();
      } catch {
        /* fall through to reconnect anyway */
      }
      socket.forceReconnect();
    };
    const onOnline = () => socketRef.current?.forceReconnect();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [access, isClinician]);

  // Push a payload for an already-rendered user bubble. socket.send returns
  // false when the socket isn't open (mid-reconnect) or the frame fails to
  // serialize; in that case flag the bubble failed and keep the payload for a
  // retry, and don't arm the typing spinner (no reply is coming). On success,
  // clear any prior failed flag and start awaiting the reply.
  const pushUserPayload = useCallback((messageId: string, payload: ClinicMessagePayload) => {
    const socket = socketRef.current;
    const ok = socket ? socket.send(payload) : false;
    if (!ok) {
      failedPayloadsRef.current.set(messageId, payload);
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, sendState: 'failed' } : m)),
      );
      return;
    }
    failedPayloadsRef.current.delete(messageId);
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId && m.sendState ? { ...m, sendState: undefined } : m)),
    );
    awaitingResponseRef.current = true;
    setTyping(true);
  }, []);

  const sendMessage = useCallback(
    (text: string, file?: { dataUrl: string; type: string; name: string }) => {
      const trimmed = text.trim();
      // Allow sending a file with no text, but never an empty message.
      if (!trimmed && !file) return;
      const isPdf = file?.type === 'application/pdf';
      const target = focusedPatientRef.current;
      const id = makeId();
      setMessages((prev) => [
        ...prev,
        {
          id,
          role: 'user',
          text: trimmed,
          ts: Date.now(),
          ...(file ? { fileUrl: file.dataUrl, fileType: file.type } : {}),
          ...(isPdf ? { fileName: file!.name } : {}),
        },
      ]);
      pushUserPayload(id, {
        type: 'message',
        text: trimmed,
        ...(target ? { patient_id: target.id } : {}),
        ...(file ? { file_data: file.dataUrl, file_type: file.type } : {}),
        // Only PDFs carry a filename (lets the model reference the document).
        ...(isPdf ? { file_name: file!.name } : {}),
      });
    },
    [pushUserPayload],
  );

  const retrySend = useCallback(
    (messageId: string) => {
      const payload = failedPayloadsRef.current.get(messageId);
      if (!payload) return;
      pushUserPayload(messageId, payload);
    },
    [pushUserPayload],
  );

  // Point focus at an explicitly chosen patient. The name is known from the
  // picker, so cache it too. Leaves the lock untouched: an unlocked pick keeps
  // following the route on the next navigation; a locked pick re-targets the pin.
  const focusPatient = useCallback((patient: FocusedPatient) => {
    patientNameCacheRef.current.set(patient.id, patient.name);
    routeSyncSeqRef.current += 1; // cancel any in-flight route name fetch
    suppressedRoutePatientIdRef.current = undefined; // an explicit pick overrides any suppression
    focusedPatientRef.current = patient; // synchronous, so an immediate send targets this patient
    setFocusedPatient(patient);
  }, []);

  // Drop the target entirely. Update the ref synchronously so any immediate send
  // omits patient_id, and suppress the open patient's page from re-selecting so
  // the clear sticks until the clinician navigates to a different patient.
  const clearFocus = useCallback(() => {
    routeSyncSeqRef.current += 1;
    focusLockedRef.current = false;
    focusedPatientRef.current = null;
    suppressedRoutePatientIdRef.current = lastRoutePatientIdRef.current;
    setFocusedPatient(null);
    setFocusLocked(false);
  }, []);

  // Mirror the route's patient onto focus, unless focus is pinned. Resolves the
  // name lazily (cache → optimistic placeholder → fetch), ignoring stale fetches.
  const syncRoutePatient = useCallback(
    (patientId?: number) => {
      lastRoutePatientIdRef.current = patientId; // remembered even while pinned
      // Navigating to a different patient than the suppressed one lifts the
      // suppression — entering a new patient always re-selects (when unlocked).
      if (
        suppressedRoutePatientIdRef.current !== undefined &&
        patientId !== suppressedRoutePatientIdRef.current
      ) {
        suppressedRoutePatientIdRef.current = undefined;
      }
      if (focusLockedRef.current) return; // pinned — route no longer drives focus
      // Leaving patient pages (dashboard, list, …) keeps the current target: the
      // focus is only cleared by the X, and only changed by entering a patient.
      if (patientId == null) return;
      // Still on the just-cleared patient's page — stay empty.
      if (patientId === suppressedRoutePatientIdRef.current) return;
      if (focusedPatientRef.current?.id === patientId) return;

      const seq = ++routeSyncSeqRef.current;
      const cached = patientNameCacheRef.current.get(patientId);
      if (cached) {
        setFocusedPatient({ id: patientId, name: cached });
        return;
      }
      setFocusedPatient({ id: patientId, name: 'Cargando…' });
      patientsService
        .getPatientById(patientId)
        .then((p) => {
          const name = `${p.firstName} ${p.lastName}`.trim() || p.username || `Paciente #${patientId}`;
          patientNameCacheRef.current.set(patientId, name);
          // Apply only if this is still the newest sync and target is unchanged.
          if (routeSyncSeqRef.current === seq && focusedPatientRef.current?.id === patientId) {
            setFocusedPatient({ id: patientId, name });
          }
        })
        .catch(() => {
          if (routeSyncSeqRef.current === seq && focusedPatientRef.current?.id === patientId) {
            setFocusedPatient({ id: patientId, name: `Paciente #${patientId}` });
          }
        });
    },
    [],
  );

  const lockFocus = useCallback(() => {
    focusLockedRef.current = true;
    setFocusLocked(true);
  }, []);

  // Update the ref synchronously so the immediate re-sync below isn't skipped by
  // the still-locked ref (state only flips on the next render).
  const unlockFocus = useCallback(() => {
    focusLockedRef.current = false;
    setFocusLocked(false);
    syncRoutePatient(lastRoutePatientIdRef.current);
  }, [syncRoutePatient]);

  // Mutate a proposal's lifecycle state by its stable proposal_id, wherever it
  // sits in the transcript (live turn or persisted history row).
  const setProposalStatusById = useCallback(
    (proposalId: string, patch: Partial<ProposalState>) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.proposals) return m;
          let changed = false;
          const proposals = m.proposals.map((p) => {
            if (p.id !== proposalId) return p;
            changed = true;
            return { ...p, ...patch };
          });
          return changed ? { ...m, proposals } : m;
        }),
      );
    },
    [],
  );

  // Resolve one or more proposals (accept/reject). The backend executes and
  // persists each action; we only send the decisions. Cards flip optimistically
  // to 'applying', then to the server's terminal state per item. On failure the
  // backend rolled that action back and the proposal stays open, so we set
  // 'error' (which still shows the buttons) to let the clinician retry — each
  // item is handled independently, so a partial batch failure only marks the
  // ones that failed. Other tabs/devices reconcile via the proposal_resolved
  // broadcast.
  const resolveManyProposals = useCallback(
    async (proposalIds: string[], decision: 'accept' | 'reject') => {
      const ids = Array.from(new Set(proposalIds.filter(Boolean)));
      if (ids.length === 0) return;
      ids.forEach((id) => setProposalStatusById(id, { status: 'applying', error: undefined }));
      try {
        const results = await resolveProposals(ids.map((id) => ({ proposal_id: id, decision })));
        const byId = new Map(results.map((r) => [r.proposal_id, r]));
        ids.forEach((id) => {
          const r = byId.get(id);
          if (r && r.ok) {
            const status: ProposalStatus =
              terminalStatus(r.status) ?? (decision === 'accept' ? 'applied' : 'discarded');
            setProposalStatusById(id, { status, error: undefined, detail: r.detail });
          } else {
            setProposalStatusById(id, {
              status: 'error',
              error: (r && r.error) || 'No se pudo aplicar la propuesta.',
            });
          }
        });
      } catch (err: any) {
        const message =
          err?.response?.detail ||
          (err?.response && typeof err.response === 'object'
            ? Object.values(err.response).flat().join(' ')
            : null) ||
          err?.message ||
          'No se pudo aplicar la propuesta.';
        ids.forEach((id) => setProposalStatusById(id, { status: 'error', error: String(message) }));
      }
    },
    [setProposalStatusById],
  );

  const resolveProposal = useCallback(
    (proposalId: string, decision: 'accept' | 'reject') => resolveManyProposals([proposalId], decision),
    [resolveManyProposals],
  );

  const isBusy = typing || statusLabel != null;

  const value = useMemo<ClinicAgentContextValue>(
    () => ({
      messages,
      connectionState,
      typing,
      statusLabel,
      isBusy,
      authError,
      sendMessage,
      retrySend,
      focusedPatient,
      focusLocked,
      lockFocus,
      unlockFocus,
      focusPatient,
      clearFocus,
      syncRoutePatient,
      resolveProposal,
      resolveManyProposals,
      hasMoreHistory,
      loadingMoreHistory,
      loadMoreHistory,
    }),
    [messages, connectionState, typing, statusLabel, isBusy, authError, sendMessage, retrySend, focusedPatient, focusLocked, lockFocus, unlockFocus, focusPatient, clearFocus, syncRoutePatient, resolveProposal, resolveManyProposals, hasMoreHistory, loadingMoreHistory, loadMoreHistory],
  );

  // Don't provide the agent UI for non-clinicians.
  if (!isClinician) {
    return <>{children}</>;
  }

  return <ClinicAgentContext.Provider value={value}>{children}</ClinicAgentContext.Provider>;
};

export const useClinicAgent = (): ClinicAgentContextValue | null => {
  return useContext(ClinicAgentContext) ?? null;
};
